import videojs from 'video.js';

import ModalDialog from '../../components/modal-dialog.js';

import generateSearchForm from './search/search-form.js';

import './search.css';
import { searchCaptionsAndSlides, SearchResultType } from '../../api/presentation.js';

import SearchIcon from '../../assets/images/search-white-24dp.svg';
import generateSearchResult from './search/search-result.js';
import { isPresentationId } from '../../util';

const _modal = new WeakMap();
const _formEl = new WeakMap();
const _resultsEl = new WeakMap();
let localize;

/**
 * The Search module.
 * @member {SearchPlugin} search
 * @memberof MediasitePlayer#
 */

/**
 * Lets the users search for and navigate to slides and captions within the presentation.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class SearchPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const modal = new ModalDialog(player, {
            temporary: false,
            uncloseable: false,
            pauseOnOpen: false,
            description: localize('Search'),
            displayAsPanel: true
        });
        _modal.set(this, modal);
        modal.addClass('mediasite-player__search-modal');
        modal.addClass('mediasite-player__control-bar-modal');
        player.controlbarMenu().addMenuItem(localize('Search'), { id: this.name, icon: SearchIcon, callback: () => this.openModal() });

        player.ready(() => {
            player.layout().addChild(modal);
        });
    }

    /**
     * Opens the Search modal dialog.
     * @example
     * player.search.openModal();
     **/
    openModal() {
        if (isPresentationId(this.player.loader().presentationId)) {
            const modal = _modal.get(this);
            const content = generateSearchForm(this.player);
            this.#setupModalContent(content);
            modal.fillWith(content);
            modal.open();
            _formEl.set(this, content);
        }
    }

    /**
     * Closes the Search modal dialog.
     * @example
     * player.search.closeModal();
     **/
    closeModal() {
        _modal.get(this).close();
    }

    /**
     * @private
     * @param {HTMLDivElement} content
     */
    #setupModalContent(content) {
        const form = content.querySelector('form');

        // When submitButton is clicked, validate and submit
        form.addEventListener('submit', event => {
            event.preventDefault();
            this.#handleSubmit();
        });
    }

    #handleSubmit() {
        /** @type {HTMLFormElement} */
        const formEl = _formEl.get(this).querySelector('form');
        const searchTerm = formEl.querySelector('input').value;

        this.#disableFormControls();

        const resultsEl = _resultsEl.get(this);
        if (resultsEl) {
            resultsEl.remove();
            _resultsEl.set(this, null);
        }

        searchCaptionsAndSlides(this.player.reporting().playbackTicket, searchTerm)
            .then(result => this.#handleSearchComplete(searchTerm, result))
            .catch(error => this.#handleSearchError(error));
    }

    /**
     * @private
     * @param {Array<{text:string,time:number,type:number}>} results
     */
    #handleSearchComplete(searchTerm, results) {
        const RESULT_TYPE_MAPPING = [
            '',
            localize('Closed caption'),
            localize('Slide')
        ];

        this.#enableFormControls();

        const resultContainer = document.createElement('ul');
        resultContainer.className = 'mediasite-player__search-results-list';
        _resultsEl.set(this, resultContainer);
        if (results.length > 0) {
            for (const result of results) {
                const resultEl = generateSearchResult(
                    result.type === SearchResultType.Caption ? result.text : localize('[Search result in slide text.]'),
                    result.time / 1000,
                    RESULT_TYPE_MAPPING[result.type],
                    results.indexOf(result),
                    this.player
                );
                resultContainer.appendChild(resultEl);
                for (const type of ['click', 'keydown']) {
                    resultEl.addEventListener(type, event => this.#handleClickedSearchResult(event));
                }
            }

            const modal = _modal.get(this);
            modal.contentEl().appendChild(resultContainer);
            resultContainer.firstElementChild?.focus();
        }
        else {
            // Todo: show "no results" message
        }

        const form = _formEl.get(this);
        form.querySelector('.mediasite-player__search-notice').textContent = `${localize('Found')} ${results.length} ${localize('results for')} '${searchTerm}'.`;
    }

    #handleSearchError() {
        this.player.showMessage(localize('There was a problem getting search results. Try refreshing the page and searching again.'));
        this.#enableFormControls();
    }

    #handleClickedSearchResult(event) {
        if (event.type === 'keydown' && !(event.keyCode === 13 || event.keyCode === 32)) {
            return;
        }

        const time = parseInt(event.currentTarget.dataset.time, 10);
        this.player.currentTime(time);
    }

    #enableFormControls() {
        const formEl = _formEl.get(this).querySelector('form');
        for (const el of Array.from(formEl.querySelectorAll('input'))) {
            el.disabled = false;
        }
    }

    #disableFormControls() {
        const formEl = _formEl.get(this).querySelector('form');
        for (const el of Array.from(formEl.querySelectorAll('input'))) {
            el.disabled = true;
        }
    }

    /**
     * A boolean indicating whether or not the Search feature is enabled in the UI.
     * @type {boolean}
     * @example
     * player.search.enabled = true;
     */
    get enabled() {
        return this.player.controlbarMenu().isMenuItemEnabled(this.name);
    }

    set enabled(enabled) {
        if (enabled) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
        }
    }

    dispose() {
        const modal = _modal.get(this);
        this.player.layout().removeChild(modal);

        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }

        super.dispose();
    }
}

export default SearchPlugin;
